import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import { Button, Snackbar, Alert } from "@mui/material";
import { getData, postData, API_ENDPOINTS } from "../../services/apiService.js";
import LoadingSpinner from "../LoadingContext.js";
import "./Settings.css";

function Profile() {
    const [admin, setAdmin] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [previewImage, setPreviewImage] = useState("");
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                setLoading(true);

                const userDataString = localStorage.getItem("userData");

                if (userDataString) {
                    const userData = JSON.parse(userDataString);
                    const adminId = userData.admin_id;

                    if (adminId) {
                        const [result] = await Promise.all([
                            getData(API_ENDPOINTS.getAdminById, { id: adminId }),
                            new Promise((resolve) => setTimeout(resolve, 3000)), // min 3s delay
                        ]);

                        const adminData = result.result;
                        setAdmin(adminData);
                        setFormData(adminData);
                        setPreviewImage(adminData.pic);
                    } else {
                        console.error("Admin ID not found within the userData object.");
                    }
                } else {
                    console.error("User data not found in local storage.");
                }
            } catch (error) {
                console.error("Failed to fetch admin data or parse JSON:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchAdminData();
    }, []);

    const handleFieldChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setPreviewImage(reader.result);
            };
            reader.readAsDataURL(file);

            setFormData((prev) => ({ ...prev, pic: file }));
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const userDataString = localStorage.getItem("userData");
            if (!userDataString) throw new Error("User data not found in local storage.");
            const userData = JSON.parse(userDataString);
            const adminId = userData.admin_id;

            const data = new FormData();
            data.append("id", adminId);
            data.append("first_name", formData.first_name || "");
            data.append("last_name", formData.last_name || "");
            data.append("mobile_num", formData.mobile_num || "");
            data.append("city", formData.city || "");
            data.append("state", formData.state || "");

            if (formData.pic && formData.pic instanceof File) {
                data.append("pic", formData.pic);
            }

            const res = await postData(API_ENDPOINTS.updateAdmin, data);

            if (res.status === "SUCCESS") {
                const updatedAdminData = await getData(API_ENDPOINTS.getAdminById, { id: adminId });
                setAdmin(updatedAdminData.result);
                setFormData(updatedAdminData.result);
                setPreviewImage(updatedAdminData.result.pic);
                setEditMode(false);
                setSnackbar({
                    open: true,
                    message: "Profile updated successfully!",
                    severity: "success",
                });
            } else {
                throw new Error(res.message || "Failed to update profile.");
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred.";
            setSnackbar({
                open: true,
                message: `Failed to update profile: ${errorMessage}`,
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading) return <LoadingSpinner isLoading={loading} />;

    if (!admin) return <div>No profile found.</div>;

    return (
        <div className="profile-container">
            <LoadingSpinner isLoading={saving} />
            <div>
                <div className="edit-conatiner">
                    <img
                        src="/smart-vectors/Edit-Profile.svg"
                        alt="Edit Profile"
                        className="edit-icon"
                        style={{ position: "absolute", width: "30px", height: "30px", cursor: "pointer" }}
                        onClick={() => setEditMode(true)}
                    />
                </div>

                <div className="img-conatiner">
                    <img
                        src={previewImage || "/images/Dummy-Image.jpg"}
                        alt="User Profile"
                        className="profile-thumbnail"
                        style={{ cursor: "pointer" }}
                    />
                </div>

                {editMode && (
                    <div className="img-conatiner">
                        <Button
                            variant="contained"
                            component="label"
                            sx={{ background: "rgba(38, 116, 233, 1)", borderRadius: "10px", width: "150px", height: "40px" }}
                        >
                            Choose File
                            <input hidden type="file" accept="image/*" onChange={handleImageChange} />
                        </Button>
                    </div>
                )}
            </div>

            <div className="profile-details">
                <div className="profile-container">
                    {[
                        { label: "First Name", key: "first_name" },
                        { label: "Last Name", key: "last_name" },
                        { label: "Locality", key: "city" },
                        { label: "Mob", key: "mobile_num" },
                        { label: "State", key: "state" },
                    ].map((field) => (
                        <div className="content" key={field.key}>
                            <div className="name">
                                <p>{field.label}</p>
                            </div>
                            <div className="value">
                                <TextField
                                    variant="outlined"
                                    fullWidth
                                    value={formData[field.key] || ""}
                                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                                    sx={inputSx}
                                    disabled={!editMode}
                                />
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {editMode && (
                <div className="img-conatiner">
                    <Button
                        variant="contained"
                        sx={{ background: "rgba(89, 50, 234, 1)", borderRadius: "10px", width: "150px", height: "40px" }}
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? "Updating..." : "Update"}
                    </Button>
                </div>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

const inputSx = {
    background: "rgba(217, 217, 217, 1)",
    borderRadius: "10px",
    "& .MuiOutlinedInput-root": {
        height: "40px",
        width: "400px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        border: "none",
    },
};


export default Profile;